import { useCallback, useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import {
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { PDFViewer } from "@react-pdf/renderer";

import { useAuth } from "@/features/authentication";
import {
  DownPaymentSummarySection,
  GeneralInformationSection,
  LensProductSection,
  PurchaseOrderLabPdf,
} from "@/features/purchase-orders";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  PageContainer,
  ResponsiveToolbarButton,
  TabLabelWithCount,
} from "@/features/ui";

export const PurchaseOrderLab = () => {
  const { referenceNumber } = useParams();
  const { outlet, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [error, setError] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const loadPurchaseOrder = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`purchase-orders/lab/${referenceNumber}`, {
        params: {
          withCreator: true,
          withBuyer: true,
          withSeller: true,
          withWarehouse: true,
          withProductVariants: true,
          withPaymentTerm: true,
          withTransactionHistories: true,
          withAvailableDownPaymentAmount: true,
          withPurchaseDownPayments: true,
        },
      });
      setPurchaseOrder(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat pesanan pembelian lensa")
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadPurchaseOrder();
  }, [loadPurchaseOrder]);

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const toolbarContent = (
    <ResponsiveToolbarButton
      label="Cetak PDF"
      variant="outlined"
      startIcon={<PdfIcon />}
      onClick={() => setPdfDialogOpen(true)}
    />
  );

  return (
    <PageContainer
      maxWidth="xl"
      title="Pesanan Pembelian Lab"
      breadcrumbs={[
        {
          title: "Pesanan Pembelian Lab",
          path: "/procurement/purchase-orders/lab",
        },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection
          purchaseOrder={purchaseOrder}
          sellerLabel="Pemasok"
        />

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, newValue) => setTabIndex(newValue)}
          >
            <Tab
              label={
                <TabLabelWithCount
                  label="Pesanan"
                  count={purchaseOrder.purchaseOrderProductVariants?.length ?? 0}
                />
              }
            />
            <Tab
              label={
                <TabLabelWithCount
                  label="Uang Muka"
                  count={purchaseOrder.purchaseDownPayments?.length ?? 0}
                />
              }
            />
          </Tabs>
          <Divider sx={{ mb: 2.5 }} />

          {tabIndex === 0 && (
            <LensProductSection
              purchaseOrderProductVariants={
                purchaseOrder.purchaseOrderProductVariants
              }
            />
          )}
          {tabIndex === 1 && (
            <DownPaymentSummarySection purchaseOrder={purchaseOrder} />
          )}
        </Paper>

        {/* PDF Dialog */}
        <Dialog
          open={pdfDialogOpen}
          onClose={() => setPdfDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
              sx: { height: "90vh" },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Pesanan Pembelian Lab - {referenceNumber}
            <IconButton onClick={() => setPdfDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "100%" }}>
            {purchaseOrder && user && (
              <PDFViewer width="100%" height="100%">
                <PurchaseOrderLabPdf
                  purchaseOrder={purchaseOrder}
                  user={user}
                />
              </PDFViewer>
            )}
          </DialogContent>
        </Dialog>
      </Stack>
    </PageContainer>
  );
};
